import styles from "./Savings.module.css";
import { type JSX, RefObject, useContext } from "react";
import {
  AppContext,
  companyPaysNothing,
  doesServiceCoverWearParts,
  getLabelForInspection,
  isUnlimited,
  resolveActiveService,
  selectPaidServices,
  selectSubsidyDependantCalculationDetails,
} from "../../state";
import { useI18n } from "../../i18n";
import { Link } from "../Link";
import { Trans } from "react-i18next";
import { ExpandableAmountRow, PlainAmountRow, RichLabelAmountRow } from "../AmountRow";
import { SubsidyDetails } from "../SubsidyDetails";
import { InsuranceHint } from "../InsuranceHint";
import { ValueWithCurrency } from "../ValueWithCurrency";
import { type TFunction } from "i18next";

type SavingsProps = {
  scrollTargetRefs: {
    employerPaidCostsRef: RefObject<HTMLTableElement | null>;
    bikeSavingsRef: RefObject<HTMLTableElement | null>;
  };
  subsidyBoxClickHandler: () => void;
};

const Savings = (props: SavingsProps): JSX.Element => {
  const { t } = useI18n();
  const state = useContext(AppContext);

  const {
    scrollTargetRefs: { employerPaidCostsRef, bikeSavingsRef },
  } = props;

  const subsidyDependantCalculationDetails = selectSubsidyDependantCalculationDetails(state);

  const { companyPays, employeePays } = selectPaidServices(state);

  const activeService = resolveActiveService(companyPays) ?? resolveActiveService(employeePays);
  const isUnlimitedService = isUnlimited(activeService);
  const wearPartsLabel = isUnlimitedService
    ? "savings.services.wearPartsUnlimited"
    : "savings.services.wearPartsLimited";
  const isServicePaidByCompany = !!resolveActiveService(companyPays);
  const hasWearParts = doesServiceCoverWearParts(isServicePaidByCompany ? companyPays : employeePays);
  const labelForInspection = getLabelForInspection(t, isServicePaidByCompany ? companyPays : employeePays);
  const doesCompanyPayNothing = companyPaysNothing(companyPays);

  const selfPaymentInsurance = state.configuration.frameContract.insurance_option_id.payer === "self";
  const netChargeIncludedExtras = getNetChargeIncludedExtrasString(t, selfPaymentInsurance, labelForInspection);
  const hasSubsidy = state.calculation.calculationDetails?.hasSubsidy;
  const showSubsidy = hasSubsidy && state.configuration.elementVisibility.subsidyDetails;
  return (
    <>
      {doesCompanyPayNothing ? null : (
        <table className={styles.savingsContent} ref={employerPaidCostsRef}>
          <tbody>
            {isServicePaidByCompany ? (
              <ExpandableAmountRow
                label={labelForInspection ?? ""}
                value={companyPays?.inspection?.rateTotal ?? 0}
                id="savingsExpandableRow-inspection"
              >
                <>
                  <p>
                    {t("savings.services.inspection", {
                      inspectionValue: subsidyDependantCalculationDetails?.inspection,
                    })}
                  </p>
                  {hasWearParts ? (
                    <p>
                      <Trans
                        i18nKey={wearPartsLabel}
                        t={t}
                        values={{ wearPartsValue: subsidyDependantCalculationDetails?.wearParts }}
                        components={{ Link: <Link id="link-wearParts" target="_blank" /> }}
                      ></Trans>
                    </p>
                  ) : null}
                </>
              </ExpandableAmountRow>
            ) : null}
            {companyPays?.insurance ? (
              <ExpandableAmountRow
                label={t("savings.services.insurance.title")}
                value={subsidyDependantCalculationDetails?.insurance}
                id="savingsExpandableRow-insurance"
              >
                <p>
                  {t("savings.services.insurance.hintInsurance")}
                  <br />
                  {t("savings.services.insurance.hintBreakdownAssistance")}
                </p>
              </ExpandableAmountRow>
            ) : null}
          </tbody>
          <tfoot>
            <RichLabelAmountRow
              htmlLabel={<label>{t("savings.services.subTotal.1")}</label>}
              isTotal
              value={companyPays?.totalCosts}
            />
          </tfoot>
        </table>
      )}
      <table className={styles.savingsContent} ref={bikeSavingsRef}>
        <tbody>
          <PlainAmountRow
            label={t("savings.bike.purchasePrice")}
            value={subsidyDependantCalculationDetails?.bikePricePrivate}
            withSublistingStyles
          >
            <span>
              {`${t("bikePriceLabel")} `}
              <ValueWithCurrency value={subsidyDependantCalculationDetails?.bikePrice ?? 0} />
            </span>
            {(subsidyDependantCalculationDetails?.serviceCostPrivate ?? 0) > 0 ? (
              <span>
                {`${labelForInspection} ${t("inclMehrwertsteuer")} `}
                <ValueWithCurrency value={subsidyDependantCalculationDetails?.serviceCostPrivate ?? 0} />
              </span>
            ) : null}
            {selfPaymentInsurance ? null : (
              <span>
                {`${t("insuranceAndBreakdownAssistance")} `}
                <ValueWithCurrency value={subsidyDependantCalculationDetails?.insurance ?? 0} />
              </span>
            )}
          </PlainAmountRow>
          <PlainAmountRow
            labelKey={"savings.bike.priceWithJobRad.1"}
            value={subsidyDependantCalculationDetails?.bikePriceWithJobRad}
            withSublistingStyles
          >
            <span>
              <ValueWithCurrency value={subsidyDependantCalculationDetails?.netChargeEmployee ?? 0} />
              {` ${t("savings.bike.priceWithJobRad.2")}`}
              <ValueWithCurrency value={(subsidyDependantCalculationDetails?.netChargeEmployee ?? 0) * 36} />
              <br />
              {netChargeIncludedExtras}
            </span>
            <span>
              {` ${t("savings.bike.priceWithJobRad.3")}`}
              <ValueWithCurrency value={subsidyDependantCalculationDetails?.restRate ?? 0} />
            </span>
          </PlainAmountRow>
        </tbody>
        <tfoot>
          <PlainAmountRow
            label={t("savings.bike.subTotal")}
            isTotal
            value={subsidyDependantCalculationDetails?.savingsBetweenPrivateAndJobrad}
          />
        </tfoot>
      </table>
      {showSubsidy && <SubsidyDetails subsidyBoxClickHandler={props.subsidyBoxClickHandler} />}

      <InsuranceHint
        selfPayment={selfPaymentInsurance}
        employeePaysInsurance={!!state.calculation.calculationDetails?.employeePays.insurance}
      />
    </>
  );
};

function getNetChargeIncludedExtrasString(
  t: TFunction,
  selfPaymentInsurance: boolean,
  labelForInspection: string | null,
) {
  if (!selectPaidServices && !labelForInspection) {
    return "";
  }

  const extras = [];

  if (labelForInspection) {
    extras.push(labelForInspection);
  }

  if (!selfPaymentInsurance) {
    extras.push(t("insuranceAndBreakdownAssistance"));
  }

  return `${t("incl")} ${extras.join(", ")}`;
}

export { Savings };
